import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";

import Wrapper from "./Wrapper";
import logo from "../../../assets/images/logo.svg";
import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../constants/routes";
import { FEEDBACK_URL, SOCIAL_LINKS, SUPPORT_URL } from "../../constants/externalLinks";

interface FooterProps {
    className?: string;
}

const Footer = ({ className }: FooterProps) => {
    const { lang, t } = useLanguage();

    return (
        <footer className={classNames("relative py-2 border-t-2 text-black", className)}>
            <Wrapper>
                <div className="flex flex-1 flex-wrap lg:flex-nowrap gap-y-12 xl:gap-y-0 gap-x-16 my-2 lg:my-10 justify-between">
                    {/* Brand, short description and social icons */}
                    <div className="w-full lg:w-2/6 flex flex-col">
                        <Link to={ROUTES.home(lang)} className="relative flex items-center mb-6">
                            <img src={logo} alt="" className="h-14 rounded-sm" />
                            <span className="text-2xl font-bold text-light-primary">
                                {t.blog.team}
                            </span>
                        </Link>

                        <p className="text-lg leading-8 text-gray-700">{t.codepro.why}</p>

                        <h3 className="text-xl font-bold mt-12 mb-6">{t.codepro.social}</h3>

                        <div className="flex items-center gap-x-5 w-full">
                            {SOCIAL_LINKS.map(({ name, url, icon }) => (
                                // Plain <a>: these leave the site, so React Router isn't involved
                                <a
                                    key={name}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={name} // Icon-only links need a text label
                                    className="hover:opacity-70 hover:-translate-y-2"
                                >
                                    <FontAwesomeIcon icon={icon} size="2xl" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Support column */}
                    <div className="w-1/2 xl:w-1/6">
                        <span className="text-2xl font-bold mb-5 block">{t.blog.team}</span>
                        <ul className="flex flex-col gap-y-1 text-lg list-none">
                            <li>
                                <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" className="hover:opacity-70">
                                    {t.codepro.support}
                                </a>
                            </li>
                            <li>
                                <Link to={ROUTES.rules(lang)} className="hover:opacity-70">
                                    {t.codepro.terms}
                                </Link>
                            </li>
                            <li>
                                <a href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" className="hover:opacity-70">
                                    {t.codepro.feedback}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Useful links column */}
                    <div className="w-1/2 xl:w-1/6">
                        <span className="text-2xl font-bold mb-5 block">{t.codepro.usefullinks}</span>
                        <ul className="flex flex-col gap-y-1 text-lg list-none">
                            <li>
                                <Link to={ROUTES.blog(lang)} className="hover:opacity-70">
                                    {t.codepro.blog}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Empty column that keeps the grid balanced on wide screens */}
                    <div className="w-1/2 xl:w-1/6" />
                </div>

                {/* Copyright line */}
                <div className="relative w-full justify-center mt-8 md:text-2xl text-lg lg:text-md flex">
                    {t.codepro.allrights}
                    <span className="flex items-center ml-2 mr-2 text-light-primary">
                        <span>{t.blog.team}</span>
                        <img src={logo} alt="" className="h-6 block mr-2 rounded-sm" />
                    </span>
                    {t.codepro.reserved}
                    &copy;
                </div>
            </Wrapper>
        </footer>
    );
};

export default Footer;